import { useEffect, useMemo, useState } from 'react';
import './maintenanceQueue.css';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../../components/AppDataGrid';
import { AppDialog } from '../../../components/AppDialog';
import { ExportMenu } from '../../../components/AppToolbar/ExportMenu';
import { useDeleteFlow, useSaveFlow } from '../../../components/confirm';
import { useNotify } from '../../../components/notifications';
import {
  addAttachment, deleteAttachment, downloadAttachment, editAttachment, fetchAttachmentCategories, uploadAttachmentFile,
  type Option, type RecordAttachment,
} from './api';

// ─────────────────────────────────────────────────────────────────────────────
// The Attachment tab of the Maintenance Record and the Maintained Record.
// Port of the G5 nested list MaintenanceTaskCheckList_MaintenanceAttachments_ListView: New · Edit · Delete · Export to
// (Link / Unlink are off in G5), with the New / Edit popup of MaintenanceAttachment_DetailView - File Name*, Category and
// the file* (Date and Operator are removed from that layout; the server stamps them).
//
// Each change is saved at once and the grid takes the list the server returns, so it never reloads the record around it
// (a reload would throw away unsaved Task Details edits). The file is uploaded FIRST and the row saves the returned
// FileData Oid: a failed save then leaves an unreferenced blob, never a row pointing at nothing.
// ─────────────────────────────────────────────────────────────────────────────

const FORM_NAME = 'Maintenance Queue';

interface Props {
  taskOid: string;
  /** The attachments the record was loaded with; a later load replaces what the grid shows. */
  initial: RecordAttachment[];
  /** Write access (MAC): New / Edit / Delete are greyed without it. */
  canWrite: boolean;
  loading?: boolean;
  /** File-name prefix of the export. */
  exportName: string;
}

export default function MaintenanceAttachments({ taskOid, initial, canWrite, loading, exportName }: Props) {
  const notify = useNotify();
  const runSave = useSaveFlow();
  const runDelete = useDeleteFlow();
  const [rows, setRows] = useState<RecordAttachment[]>(initial);
  const [sel, setSel] = useState<Set<string | number>>(new Set());
  const [dialog, setDialog] = useState<{ row: RecordAttachment | null } | null>(null);

  useEffect(() => { setRows(initial); setSel(new Set()); }, [initial]);

  const one = sel.size === 1 ? rows.find((r) => r.oid === String([...sel][0])) ?? null : null;

  const columns: AppColumn<RecordAttachment>[] = useMemo(() => [
    { field: 'fileName', header: 'FileName', width: 220 },
    { field: 'category', header: 'Category', width: 160 },
    { field: 'attachment', header: 'Attachment', width: 220, sortable: false,
      render: (r) => (r.fileId
        ? <a href="#" onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            downloadAttachment(r.fileId!, r.attachment).catch(() => notify.error('Could not download the file.'));
          }}>{r.attachment ?? 'Download'}</a>
        : <span style={{ color: 'var(--ai-muted)' }}>—</span>) },
    { field: 'date', header: 'Date', width: 130, type: 'datetime' },
    { field: 'operator', header: 'Operator', width: 160 },
  ], [notify]);

  function remove() {
    if (!one) return;
    void runDelete({
      formName: FORM_NAME,
      message: `Delete the attachment '${one.fileName ?? ''}'?`,
      delete: async () => { setRows(await deleteAttachment(one.oid)); },
      onDeleted: () => setSel(new Set()),
    });
  }

  return (
    <>
      <div className="mq-attbar">
        <button type="button" className="ca-btn" disabled={!canWrite} onClick={() => setDialog({ row: null })} title="New">
          <i className="ti ti-file-plus tb-i-green" aria-hidden="true" /> New
        </button>
        <button type="button" className="ca-btn" disabled={!canWrite || !one} onClick={() => one && setDialog({ row: one })} title="Edit">
          <i className="ti ti-pencil tb-i-blue" aria-hidden="true" /> Edit
        </button>
        <button type="button" className="ca-btn" disabled={!canWrite || !one} onClick={remove} title="Delete">
          <i className="ti ti-x tb-i-red" aria-hidden="true" /> Delete
        </button>
        <ExportMenu filename={exportName} {...gridExportMatrix(rows, columns)} triggerClassName="ca-btn" />
      </div>
      <AppDataGrid<RecordAttachment>
        rows={rows}
        columns={columns}
        getRowId={(r) => r.oid}
        loading={loading}
        selectable
        selectedIds={sel}
        onSelectionChange={setSel}
        onRowDoubleClick={canWrite ? (r) => setDialog({ row: r }) : undefined}
        defaultPageSize={10}
        maximizable
        hideHeaderExport
        exportFilename={exportName}
        emptyText="No data to display"
      />
      {dialog && (
        <AttachmentDialog
          taskOid={taskOid}
          row={dialog.row}
          runSave={runSave}
          onClose={() => setDialog(null)}
          onSaved={(list) => { setRows(list); setSel(new Set()); setDialog(null); }}
        />
      )}
    </>
  );
}

interface DialogProps {
  taskOid: string;
  /** The row being edited; null = a new attachment. */
  row: RecordAttachment | null;
  runSave: ReturnType<typeof useSaveFlow>;
  onClose: () => void;
  onSaved: (list: RecordAttachment[]) => void;
}

function AttachmentDialog({ taskOid, row, runSave, onClose, onSaved }: DialogProps) {
  const notify = useNotify();
  const [fileName, setFileName] = useState(row?.fileName ?? '');
  const [categoryId, setCategoryId] = useState(row?.categoryId ?? '');
  const [file, setFile] = useState<File | null>(null);
  const [categories, setCategories] = useState<Option[]>([]);

  useEffect(() => {
    let alive = true;
    fetchAttachmentCategories()
      .then((c) => { if (alive) setCategories(c); })
      .catch(() => notify.error('Could not load the categories.'));
    return () => { alive = false; };
  }, [notify]);

  function submit() {
    // The same wording G5's required-field rules use.
    if (!fileName.trim()) { notify.error("'FileName' must not be empty."); return; }
    if (!file && !row) { notify.error("'Attachment' must not be empty."); return; }
    let result: RecordAttachment[] = [];
    void runSave({
      formName: FORM_NAME,
      auditAction: row ? 'edit' : 'create',
      save: async () => {
        // On an edit with no new file the row keeps its current one (fileId stays null).
        const fileId = file ? await uploadAttachmentFile(file) : null;
        const body = { fileName: fileName.trim(), categoryId: categoryId || null, fileId };
        result = row ? await editAttachment(row.oid, body) : await addAttachment(taskOid, body);
      },
      onSaved: () => onSaved(result),
    });
  }

  return (
    <AppDialog
      open
      onClose={onClose}
      title={`${row ? 'Edit' : 'New'} Attachment`}
      icon="ti-paperclip"
      width={480}
      footer={<>
        <button type="button" className="ca-btn" onClick={onClose}>Cancel</button>
        <button type="button" className="ca-btn ca-btn-solid" onClick={submit}>
          <i className="ti ti-device-floppy" aria-hidden="true" /> Save
        </button>
      </>}
    >
      <div className="mq-att-grid">
        <label className="req" htmlFor="mqa-name">File Name</label>
        <input id="mqa-name" value={fileName} onChange={(e) => setFileName(e.target.value)} maxLength={200} />
        <label htmlFor="mqa-cat">Category</label>
        <select id="mqa-cat" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
          <option value="" />
          {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <label className={row ? undefined : 'req'} htmlFor="mqa-file">Attachment</label>
        <input id="mqa-file" type="file" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
        {row?.attachment && !file && <div className="mq-att-current">Current file: {row.attachment}</div>}
      </div>
    </AppDialog>
  );
}
